import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  useDeleteHudrationMutation,
  useLazyGetHudrationQuery,
} from "../../services/hydration&Goal";
import { isValidInput } from "../../utils/validations";
import { showError, showToast } from "../../constants/toasts";
import usePermission from "../../hooks/usePermission";

const HydrationGoal = () => {
  const navigate = useNavigate();

  // Permission hook for Hydration And Goal module
  const { 
    hasPermission, 
    isLoading: permissionLoading, 
    canView, 
    canAddEdit, 
    canDelete 
  } = usePermission({
    module: "Hydration And Goal",
    permission_type: "can_view"
  });

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [getHarydation] = useLazyGetHudrationQuery();
  const [deleteHarydation] = useDeleteHudrationMutation();

  const getHardationList = async () => {
    setIsLoading(true);
    try {
      const response = await getHarydation({
        search: debouncedSearchTerm,
      }).unwrap();
      if (response?.status === 200) {
        setData(response?.data || []);
        setTotalCount(Array.isArray(response?.data) ? response.data.length : 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.log(error);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getHardationList();
  }, [debouncedSearchTerm]);

  const columns = [
    { id: "ageGroup", label: "Age Group" },
    { id: "hydrationTarget", label: "Hydration Target" },
    { id: "goalTemplate", label: "Goal Template" },
    { id: "missedGoals", label: "Missed Goals" },
    { id: "suggestion", label: "Smart Suggestion" },
    { id: "action", label: "Action" },
  ];

  const [searchTerm, setSearchTerm] = useState("");

  const handleDelete = async (id: number) => {
    if (!id) return;
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) return;
    try {
      await deleteHarydation({ id }).unwrap();
      showToast("Deleted successfully");
      getHardationList();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete");
    }
  };

  // Show loading while checking permissions
  if (permissionLoading) {
    return <div>Loading permissions...</div>;
  }

  // Show access denied if user doesn't have view permission
  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <>
      <div className="main_title">
        <h1>Hydration & Goal Settings</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Hydration & Goal Settings
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          {canAddEdit("Hydration And Goal") && (
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/hydration/add")}
              >
                <AddIcon /> Add{" "}
              </Button>
            </div>
          )}
        </div>
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                {columns.slice(0, -1).map((col) => (
                  <TableCell key={col.id}>{col.label}</TableCell>
                ))}
                {(canAddEdit("Hydration And Goal") || canDelete("Hydration And Goal")) && (
                  <TableCell key="action">Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>{row.age_group || row.ageGroup || row.age}</TableCell>
                  <TableCell>{row.daily_hydration || row.hydrationTarget}</TableCell>
                  <TableCell>{row.goal_type || row.goalTemplate}</TableCell>
                  <TableCell>{row.missing_limit || row.missedGoals}</TableCell>
                  <TableCell>
                    {row.smart_suggestion || row.suggestion ? (
                      <span className="status_btn c_warning">
                        {row.smart_suggestion || row.suggestion}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  {(canAddEdit("Hydration And Goal") || canDelete("Hydration And Goal")) && (
                    <TableCell>
                      <div className="table_actions v2">
                        {canAddEdit("Hydration And Goal") && (
                          <Box>
                            <IconButton onClick={() => navigate(`/hydration/add?id=${row.id}`)}>
                              <img src="/static/images/edit2_icon.svg" alt="Edit" />
                            </IconButton>
                          </Box>
                        )}
                        {canDelete("Hydration And Goal") && (
                          <Box>
                            <IconButton onClick={() => handleDelete(row.id)}>
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </Box>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default HydrationGoal;
